import type { TimeseriesInstrumentStatus } from "@repo/shared/types";
import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";
import { LineChart } from "@/components/charts/line-chart";
import { HistoricalStatisticsPanel } from "@/components/historical-statistics";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useBrandColor } from "@/lib/context/brand-color-context";
import {
  useTimeseriesInstrumentData,
  useTimeseriesInstrumentStatistics,
} from "@/lib/hooks/use-timeseries-admin";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { statusSummary } from "./status-summary";
import { T } from "./timeseries-inspect-dialog.i18n";

type TimeseriesInspectDialogProps = {
  open: boolean;
  item: TimeseriesInstrumentStatus | null;
  onClose: () => void;
};

/**
 * Admin QA view of an instrument's raw stored data points: the chart, and
 * (QL-706) the calendar statistics of the stored series as it is — the
 * instrument's own currency, no patch, no conversion.
 */
export function TimeseriesInspectDialog({ open, item, onClose }: TimeseriesInspectDialogProps) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const brandColor = useBrandColor();
  const [tab, setTab] = useState<"chart" | "statistics">("chart");

  // Back to the Chart tab on close, so the next instrument opens on it. The
  // dialog stays mounted between instruments; resetting on open would come a
  // render too late and fetch the new instrument's statistics unasked.
  useEffect(() => {
    if (!open) setTab("chart");
  }, [open]);

  const source = open ? (item?.source ?? null) : null;
  const ticker = open ? (item?.ticker ?? null) : null;
  const { data: series, isLoading } = useTimeseriesInstrumentData(source, ticker);
  const { data: stats, isLoading: statsLoading } = useTimeseriesInstrumentStatistics(
    source,
    ticker,
    tab === "statistics",
  );

  const points = series?.data ?? [];
  const first = points[0];
  const last = points[points.length - 1];
  const summary = item ? statusSummary(item, userLanguage) : null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-5xl">
        {/* Header is always rendered so DialogContent always has a Title/Description
            (Radix a11y requirement) — even during the loading state. */}
        <DialogHeader>
          <DialogTitle>{item?.name || item?.ticker || ""}</DialogTitle>
          <DialogDescription>{item ? `${item.source}:${item.ticker}` : ""}</DialogDescription>
        </DialogHeader>

        {summary && summary.level !== "ok" && (
          <Alert variant={summary.level === "error" ? "destructive" : "warning"}>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {summary.reasons.map((r) => (
                <div key={r}>{r}</div>
              ))}
            </AlertDescription>
          </Alert>
        )}

        <Tabs value={tab} onValueChange={(v) => setTab(v as "chart" | "statistics")}>
          <TabsList>
            <TabsTrigger value="chart">{t.chart}</TabsTrigger>
            <TabsTrigger value="statistics">{t.statistics}</TabsTrigger>
          </TabsList>
          <TabsContent value="chart" className="mt-4">
            {isLoading ? (
              <div className="py-8 text-center text-muted-foreground">{t.loading}</div>
            ) : !first || !last ? (
              <div className="py-8 text-center text-muted-foreground">{t.noData}</div>
            ) : (
              <div className="space-y-4">
                <LineChart
                  data={points}
                  width="container"
                  height={300}
                  lang={userLanguage}
                  primaryColor={brandColor ?? undefined}
                />

                <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
                  <span>
                    {t.firstDataPoint}: {first.date} ({first.value.toFixed(2)})
                  </span>
                  <span>
                    {t.lastDataPoint}: {last.date} ({last.value.toFixed(2)})
                  </span>
                  {item?.lastUpdate && (
                    <span>
                      {t.lastSync}: {item.lastUpdate}
                    </span>
                  )}
                  {item?.currency && (
                    <span>
                      {t.currency}: {item.currency}
                    </span>
                  )}
                  <span>
                    {t.dataPoints}: {points.length}
                  </span>
                </div>
              </div>
            )}
          </TabsContent>
          <TabsContent value="statistics" className="mt-4">
            {statsLoading ? (
              <div className="py-8 text-center text-muted-foreground">{t.loadingStatistics}</div>
            ) : !stats ? (
              <div className="py-8 text-center text-muted-foreground">{t.noStatistics}</div>
            ) : (
              <HistoricalStatisticsPanel
                statistics={stats.statistics}
                currency={stats.currency ?? item?.currency ?? null}
                source="stored"
                measuredTo={stats.measuredTo}
                footer={
                  <>
                    {stats.currency && (
                      <span>
                        {t.currency}: {stats.currency}
                      </span>
                    )}
                    <span>
                      {t.monthlyPoints}: {stats.statistics.monthlyPoints}
                    </span>
                    {stats.lastPrice && (
                      <span>
                        {t.lastPrice}: {stats.lastPrice.value.toFixed(2)}
                        {stats.currency ? ` ${stats.currency}` : ""}
                        {stats.lastPrice.date ? ` (${stats.lastPrice.date})` : ""}
                      </span>
                    )}
                  </>
                }
              />
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
